import { useEffect, useState } from 'react';

import type { AssetResource } from '@/hook/use_app_assets';
import { get_asset_preview_element_id } from '@/lib/asset-preview-id';

export function use_current_asset_sequence_number(
  asset_resources: AssetResource[],
) {
  const [current_asset_sequence_number, set_current_asset_sequence_number] =
    useState(asset_resources[0]?.asset_sequence_number ?? 1);

  useEffect(() => {
    let animation_frame_id: number | null = null;

    const update_current_asset_sequence_number = () => {
      animation_frame_id = null;

      const visible_asset_sequence_number =
        get_visible_asset_sequence_number(asset_resources);

      if (visible_asset_sequence_number === null) {
        return;
      }

      set_current_asset_sequence_number(visible_asset_sequence_number);
    };

    const request_current_asset_sequence_number_update = () => {
      if (animation_frame_id !== null) {
        return;
      }

      animation_frame_id = window.requestAnimationFrame(
        update_current_asset_sequence_number,
      );
    };

    request_current_asset_sequence_number_update();
    window.addEventListener(
      'resize',
      request_current_asset_sequence_number_update,
    );
    window.addEventListener(
      'scroll',
      request_current_asset_sequence_number_update,
      { passive: true },
    );

    return () => {
      if (animation_frame_id !== null) {
        window.cancelAnimationFrame(animation_frame_id);
      }

      window.removeEventListener(
        'resize',
        request_current_asset_sequence_number_update,
      );
      window.removeEventListener(
        'scroll',
        request_current_asset_sequence_number_update,
      );
    };
  }, [asset_resources]);

  return current_asset_sequence_number;
}

function get_visible_asset_sequence_number(asset_resources: AssetResource[]) {
  const viewport_center_y = window.innerHeight / 2;
  const visible_asset_distances = asset_resources
    .map((asset_resource, asset_index) =>
      create_visible_asset_distance({
        asset_index,
        asset_resource,
        viewport_center_y,
      }),
    )
    .filter(
      (
        visible_asset_distance,
      ): visible_asset_distance is {
        asset_sequence_number: number;
        distance_to_viewport_center_y: number;
      } => visible_asset_distance !== null,
    );

  const nearest_visible_asset_distance = visible_asset_distances.reduce<
    | {
        asset_sequence_number: number;
        distance_to_viewport_center_y: number;
      }
    | null
  >((nearest_asset_distance, visible_asset_distance) => {
    if (
      nearest_asset_distance === null ||
      visible_asset_distance.distance_to_viewport_center_y <
        nearest_asset_distance.distance_to_viewport_center_y
    ) {
      return visible_asset_distance;
    }

    return nearest_asset_distance;
  }, null);

  return nearest_visible_asset_distance?.asset_sequence_number ?? null;
}

function create_visible_asset_distance({
  asset_index,
  asset_resource,
  viewport_center_y,
}: {
  asset_index: number;
  asset_resource: AssetResource;
  viewport_center_y: number;
}) {
  const asset_element = document.getElementById(
    get_asset_preview_element_id(asset_index),
  );

  if (!asset_element) {
    return null;
  }

  const asset_rect = asset_element.getBoundingClientRect();

  if (asset_rect.bottom < 0 || asset_rect.top > window.innerHeight) {
    return null;
  }

  return {
    asset_sequence_number: asset_resource.asset_sequence_number,
    distance_to_viewport_center_y: Math.abs(
      asset_rect.top + asset_rect.height / 2 - viewport_center_y,
    ),
  };
}
